import { __decorate, __metadata } from "tslib";
// NPM
import { Component, Mixins, Prop } from "vue-property-decorator";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import { IUpdatesTimelineEntryDTO } from "@/store/models/clientUpdates/updatesTimelineEntry";
import { toaster } from "@/utilities/toaster";
import { canModifyActionItem, formatDate, formatUpdateHtml, getInitials, highlightParts, NOTES_LABEL, sameId, UPDATE_SIDE_DEVELOPMENT_ID } from "../updatesHelpers";
import UpdateActionItems from "./UpdateActionItems.vue";
import UpdateLinks from "./UpdateLinks.vue";
import UpdateAttachments from "./UpdateAttachments.vue";
/**
 * One update on the timeline: date, rail dot, header, text, author and the Details panel.
 * Which rows are open lives in UpdatesTimeline, so it survives a row being filtered out and back.
 */
let UpdateTimelineItem = /** @class */ (() => {
    var _a;
    let UpdateTimelineItem = class UpdateTimelineItem extends Mixins(BaseMixer) {
        constructor() {
            super(...arguments);
            this.truncated = false;
            // Author photo failed to load - falls back to initials
            this.imageBroken = false;
            this.formatDate = formatDate;
            this.getInitials = getInitials;
        }
        get showAuthorImage() {
            return !!this.entry.createdByImageURL && !this.imageBroken;
        }
        get formattedHtml() {
            return formatUpdateHtml(this.entry.updateHtml, this.searchTerm);
        }
        get isDevelopment() {
            return sameId(this.entry.updateSideId, UPDATE_SIDE_DEVELOPMENT_ID);
        }
        get interactionLabel() {
            return this.entry.interactionTypeLabel || NOTES_LABEL;
        }
        get actionItems() {
            return this.entry.actionItems || [];
        }
        get links() {
            return (this.entry.attachments || []).filter((a) => !a.mediaFileId && !!a.url);
        }
        get files() {
            return (this.entry.attachments || []).filter((a) => !!a.mediaFileId);
        }
        get hasActionItems() {
            return this.entry.hasActionItems || this.actionItems.length > 0;
        }
        get hasLinks() {
            return this.entry.hasLinks || this.links.length > 0;
        }
        get hasAttachments() {
            return this.entry.hasAttachments || this.files.length > 0;
        }
        get hasDetails() {
            // On the Action Items tab the items are already shown, so Details is only links and files
            if (this.collapseUpdates) {
                return this.hasLinks || this.hasAttachments;
            }
            return this.hasActionItems || this.hasLinks || this.hasAttachments;
        }
        get isCopyVisible() {
            return !this.collapseUpdates || this.updateShown;
        }
        hl(text) {
            return highlightParts(text, this.searchTerm);
        }
        onAuthorImageError() {
            this.imageBroken = true;
        }
        setActionItemStatus(payload) {
            this.$emit("set-action-item-status", payload);
        }
        toggleDetails() {
            this.$emit("toggle-details", this.entry);
        }
        toggleUpdate() {
            this.$emit("toggle-update", this.entry);
        }
        toggleMenu() {
            this.$emit("toggle-menu", this.entry);
        }
        toggleSummaryPop() {
            this.$emit("toggle-summary", this.entry);
        }
        editUpdate() {
            this.$emit("close-menu");
            this.$emit("edit-update", this.entry);
        }
        deleteUpdate() {
            this.$emit("close-menu");
            // Deleting the update deletes its action items, so it needs the right to delete each of them (the API checks too)
            if (this.actionItems.some((item) => !canModifyActionItem(item))) {
                toaster.ShowError("This update has action items owned by other users and can't be deleted.");
                return;
            }
            this.$emit("delete-update", this.entry);
        }
        // A clamped summary that overflows gets a Read more link on the line below
        measureTruncation() {
            const el = this.$refs.summary;
            const truncated = this.isCopyVisible && !!el && el.scrollHeight > el.clientHeight + 1;
            if (truncated !== this.truncated) {
                this.truncated = truncated;
            }
        }
        onResize() {
            this.measureTruncation();
        }
        mounted() {
            window.addEventListener("resize", this.onResize);
            this.$nextTick(() => this.measureTruncation());
        }
        updated() {
            this.$nextTick(() => this.measureTruncation());
        }
        beforeDestroy() {
            window.removeEventListener("resize", this.onResize);
        }
    };
    __decorate([
        Prop({ required: true }),
        __metadata("design:type", typeof (_a = typeof IUpdatesTimelineEntryDTO !== "undefined" && IUpdatesTimelineEntryDTO) === "function" ? _a : Object)
    ], UpdateTimelineItem.prototype, "entry", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], UpdateTimelineItem.prototype, "searchTerm", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "statusBusy", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "readOnly", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "collapseUpdates", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "detailOpen", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "updateShown", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "menuOpen", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], UpdateTimelineItem.prototype, "summaryOpen", void 0);
    UpdateTimelineItem = __decorate([
        Component({
            name: "UpdateTimelineItem",
            components: {
                UpdateActionItems,
                UpdateLinks,
                UpdateAttachments,
            },
        })
    ], UpdateTimelineItem);
    return UpdateTimelineItem;
})();
export default UpdateTimelineItem;
